import React, { useState } from "react";
import AddGenericModal from "../../Modals/AddGenericModal";
import { patientFields, PatientFormData } from "../../types/patient";

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PatientFormData) => Promise<void>;
}

const initialFormState = patientFields.reduce((acc, field) => {
  acc[field.name as keyof PatientFormData] = "";
  return acc;
}, {} as PatientFormData);

export const AddPatientModal: React.FC<AddPatientModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<PatientFormData>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    const fieldDef = patientFields.find((f) => f.name === name);

    setFormData((prev) => {
      let finalValue: any = value;

      // Έλεγχος αν το πεδίο είναι αριθμητικό (number, numeric select ή τηλέφωνο που ορίζεται ως int στο backend)
      const isNumeric =
        fieldDef?.type === "number" ||
        name === "nearestTelephone1" ||
        name === "nearestTelephone2" ||
        name === "weight";

      if (isNumeric) {
        // Στέλνουμε null αν είναι κενό string "", αλλιώς μετατρέπουμε σε Number
        finalValue = value === "" ? null : Number(value);
      }

      return { ...prev, [name]: finalValue };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Μετατροπές keys σε PascalCase και κενών string σε null
    const formattedData: Record<string, any> = {};

    Object.keys(formData).forEach((key) => {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      const value = formData[key as keyof PatientFormData];

      // Αν είναι κενό string, στέλνουμε null για να μην σκάει ο .NET serializer
      formattedData[capitalizedKey] = value === "" ? null : value;
    });

    try {
      setIsSubmitting(true);
      await onSubmit(formattedData as PatientFormData);
      setFormData(initialFormState);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <AddGenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Προσθήκη Νέου Ασθενή"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {patientFields.map((field) => {
            const isSelect = field.type === "select" || Boolean(field.options);

            return (
              <div key={field.name} className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {field.label}{" "}
                  {field.required && <span className="text-red-500">*</span>}
                </label>

                {isSelect ? (
                  <select
                    name={field.name}
                    value={formData[field.name as keyof PatientFormData] ?? ""}
                    onChange={handleChange}
                    required={field.required}
                    className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white text-sm cursor-pointer"
                  >
                    <option value="">Επιλέξτε...</option>
                    {field.options?.map((opt) => (
                      <option key={String(opt.value)} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name as keyof PatientFormData] ?? ""}
                    onChange={handleChange}
                    required={field.required}
                    className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition text-sm font-medium"
          >
            Ακύρωση
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-5 py-2 bg-primary text-white rounded-md hover:bg-darkprimary transition disabled:opacity-50 text-sm font-medium"
          >
            {isSubmitting ? "Αποθήκευση..." : "Αποθήκευση"}
          </button>
        </div>
      </form>
    </AddGenericModal>
  );
};
